/**
 * BAB Report API client. The report figures are read-only; the only write path is the
 * comment table [BabProcess].[Comment], which is shared with the BAB Process App.
 */
import { fetchApi } from './api';
import type {
    BabPeriod, BabReportDimensions, BabYtdResponse, BabMtdResponse,
    BabLineAccount, BabTransactionsResponse, BabComment, BabCommentTarget, BabReportFilters,
    BabCommentInbox, BabCommentInboxFilters,
    BabDetailAccessMatrix, BabMtdAccountMatrix,
    BabQuery, BabQueryDetail, BabQueryCreateInput, BabQueryStatus,
    BabQueryInboxItem, BabAssignableUser, BabQueryAnchorLevel, BabQueryHistoryItem,
    BabQueryAttachment,
} from '../types/babReport';

/**
 * Pass as `buCode` to mean "every business unit of this company on this line".
 *
 * The MTD account matrix aggregates BuCode away, so one of its rows can span several BUs and
 * has no single code to drill with. Must stay in step with `ALL_BUSINESS_UNITS` in
 * `api/src/functions/bab-report/account-detail-sql.ts`.
 */
export const ALL_BUSINESS_UNITS = 'ALL';

/**
 * The months a view has on screen, as a reference-period range. Comments and queries are
 * loaded by this, not by the report period they were written in.
 */
export interface PeriodWindow {
    fromYear: number;
    fromMonth: number;
}

/** Build the shared query string from the active filters. */
function filterParams(f: {
    babCompanyCode?: string; buCode?: string; sourceCompany?: string;
}): string {
    const q = new URLSearchParams();
    if (f.babCompanyCode) q.set('babCompanyCode', f.babCompanyCode);
    if (f.buCode) q.set('buCode', f.buCode);
    if (f.sourceCompany) q.set('sourceCompany', f.sourceCompany);
    return q.toString();
}

/** Translate the page filter state into API filter params. */
export function toApiFilters(f: BabReportFilters): { babCompanyCode?: string; buCode?: string; sourceCompany?: string } {
    return {
        babCompanyCode: f.selectedBabCompanyCode !== 'ALL' ? f.selectedBabCompanyCode : undefined,
        buCode: f.selectedBuCode !== 'ALL' ? f.selectedBuCode : undefined,
        sourceCompany: f.selectedSourceCompany || undefined,
    };
}

export const babReportApi = {
    getPeriods(): Promise<BabPeriod[]> {
        return fetchApi<BabPeriod[]>('/bab-report/periods');
    },

    getDimensions(): Promise<BabReportDimensions> {
        return fetchApi<BabReportDimensions>('/bab-report/reports');
    },

    getYtdReport(year: number, month: number, filters: ReturnType<typeof toApiFilters>): Promise<BabYtdResponse> {
        const q = new URLSearchParams({ year: String(year), month: String(month), view: 'ytd' });
        const fp = filterParams(filters);
        return fetchApi<BabYtdResponse>(`/bab-report/report?${q}${fp ? `&${fp}` : ''}`);
    },

    getMtdReport(year: number, month: number, months: number, filters: ReturnType<typeof toApiFilters>): Promise<BabMtdResponse> {
        const q = new URLSearchParams({ year: String(year), month: String(month), view: 'mtd', months: String(months) });
        const fp = filterParams(filters);
        return fetchApi<BabMtdResponse>(`/bab-report/report?${q}${fp ? `&${fp}` : ''}`);
    },

    getLineAccounts(year: number, month: number, lineCode: string, filters: ReturnType<typeof toApiFilters>): Promise<BabLineAccount[]> {
        const q = new URLSearchParams({ year: String(year), month: String(month), lineCode });
        const fp = filterParams(filters);
        return fetchApi<BabLineAccount[]>(`/bab-report/line-accounts?${q}${fp ? `&${fp}` : ''}`);
    },

    /** Account x month matrix behind one Sum line of the MTD view (freeze diffs, no bookings). */
    getLineMtdAccounts(
        year: number, month: number, months: number, lineCode: string,
        filters: ReturnType<typeof toApiFilters>
    ): Promise<BabMtdAccountMatrix> {
        const q = new URLSearchParams({
            year: String(year), month: String(month), months: String(months), lineCode,
        });
        const fp = filterParams(filters);
        return fetchApi<BabMtdAccountMatrix>(`/bab-report/line-mtd-accounts?${q}${fp ? `&${fp}` : ''}`);
    },

    /**
     * The URL of ONE section of the invoice behind ONE booking.
     *
     * The whole drill key travels, not just a document id, and that is deliberate on the
     * server side: it is both the only indexed path into ActualTransaction and the boundary
     * that closes the reachable set to what this modal could actually show. See
     * `buildPostingDocumentLookupQuery`. Never add fileCabinetId or docId here, however
     * convenient it looks — the server derives them and must keep deriving them.
     */
    documentPdfUrl(
        year: number, month: number, lineCode: string,
        companyCode: string, buCode: string, accountCode: string,
        sourceId: string, documentId: number, section: number
    ): string {
        const q = new URLSearchParams({
            year: String(year), month: String(month), lineCode, companyCode, buCode, accountCode,
            sourceId, documentId: String(documentId), section: String(section),
        });
        return `/api/v1/bab-report/document-pdf?${q}`;
    },

    /**
     * Fetch one section as a Blob.
     *
     * A plain `fetch`, not `fetchApi`, for the same reason `exportOpenQueries` below uses one:
     * the body is bytes, not the JSON envelope. The difference is the failure path — this
     * endpoint answers errors AS the envelope, and those messages are specific and worth
     * showing ("this document is no longer in DocuWare", "section 3 could not be rendered").
     * Throwing `Request failed: 404` instead would discard the only useful part of the answer.
     */
    async fetchDocumentPdf(url: string): Promise<Blob> {
        const res = await fetch(url);
        const contentType = res.headers.get('content-type') || '';
        if (res.ok && contentType.includes('application/pdf')) return res.blob();

        if (contentType.includes('application/json')) {
            const body = await res.json().catch(() => null);
            const message = body?.error?.message;
            if (typeof message === 'string' && message) throw new Error(message);
        }
        // An HTML body here is the SWA gateway, not our endpoint — usually a timeout.
        throw new Error(
            res.status === 504 || res.status === 502
                ? 'The document could not be fetched. Please try again shortly.'
                : `The document could not be opened (${res.status}).`
        );
    },

    getAccountTransactions(
        year: number, month: number, lineCode: string,
        companyCode: string, buCode: string, accountCode: string
    ): Promise<BabTransactionsResponse> {
        const q = new URLSearchParams({
            year: String(year), month: String(month), lineCode, companyCode, buCode, accountCode,
        });
        return fetchApi<BabTransactionsResponse>(`/bab-report/account-transactions?${q}`);
    },

    /**
     * Comments whose REFERENCE period falls inside `window` — the months on screen. The YTD
     * view passes January..selected month (the months its cumulative figure is made of); the
     * MTD view passes its own column window, which can reach back over the year boundary.
     * Omitting `window` means the year to date.
     */
    getComments(
        year: number, month: number,
        filters: ReturnType<typeof toApiFilters>,
        window?: PeriodWindow
    ): Promise<BabComment[]> {
        const q = new URLSearchParams({ year: String(year), month: String(month) });
        if (window) {
            q.set('fromYear', String(window.fromYear));
            q.set('fromMonth', String(window.fromMonth));
        }
        const fp = filterParams(filters);
        return fetchApi<BabComment[]>(`/bab-report/comments?${q}${fp ? `&${fp}` : ''}`);
    },

    /**
     * The scope is derived server-side from the target: a line comment stores the current
     * report filters (ALL → NULL = group-wide), an account/booking comment stores the row's
     * own company/BU/account.
     */
    createComment(
        year: number, month: number,
        target: BabCommentTarget, commentText: string,
        filters: ReturnType<typeof toApiFilters>
    ): Promise<{ commentId: number }> {
        return fetchApi<{ commentId: number }>('/bab-report/comments', {
            method: 'POST',
            body: JSON.stringify({
                periodYear: year,
                periodMonth: month,
                lineCode: target.lineCode,
                commentText,
                companyCode: target.companyCode,
                buCode: target.buCode,
                accountCode: target.accountCode,
                transactionRef: target.transactionRef,
                filterBabCompanyCode: filters.babCompanyCode,
                filterBuCode: filters.buCode,
                filterSourceCompany: filters.sourceCompany,
                // Only set when the user clicked a specific MTD month. The server defaults it
                // to the report period, and overrides it outright at booking level.
                refPeriodYear: target.refPeriodYear,
                refPeriodMonth: target.refPeriodMonth,
            }),
        });
    },

    /**
     * The Comments tab: every comment across periods, filtered server-side. Sorting happens
     * in the browser — the set is bounded and re-sorting has to be instant.
     */
    getCommentInbox(f: BabCommentInboxFilters): Promise<BabCommentInbox> {
        const q = new URLSearchParams();
        if (f.fromYear && f.fromMonth) { q.set('fromYear', String(f.fromYear)); q.set('fromMonth', String(f.fromMonth)); }
        if (f.toYear && f.toMonth) { q.set('toYear', String(f.toYear)); q.set('toMonth', String(f.toMonth)); }
        if (f.level) q.set('level', f.level);
        if (f.lineCode) q.set('lineCode', f.lineCode);
        if (f.sourceCompany) q.set('sourceCompany', f.sourceCompany);
        if (f.accountCode) q.set('accountCode', f.accountCode);
        if (f.search) q.set('search', f.search);
        // `mine` wins over a typed author — the checkbox is the stronger statement.
        if (f.mine) q.set('mine', '1');
        else if (f.author) q.set('author', f.author);
        return fetchApi<BabCommentInbox>(`/bab-report/comments/inbox?${q}`);
    },

    updateComment(commentId: number, commentText: string): Promise<{ commentId: number }> {
        return fetchApi<{ commentId: number }>(`/bab-report/comments/${commentId}`, {
            method: 'PUT',
            body: JSON.stringify({ commentText }),
        });
    },

    deleteComment(commentId: number): Promise<{ commentId: number }> {
        return fetchApi<{ commentId: number }>(`/bab-report/comments/${commentId}`, { method: 'DELETE' });
    },

    // ---- Rueckfragen ("queries", migration 138) ----
    // Reads need CanView, every write needs CanEdit. Booking-level rows are cut to the
    // caller's [Reporting].[BabDetailAccess] scope server-side, in SQL.

    /** Same reference-period window as `getComments`. */
    getQueries(
        year: number, month: number,
        filters: ReturnType<typeof toApiFilters>,
        window?: PeriodWindow
    ): Promise<BabQuery[]> {
        const q = new URLSearchParams({ year: String(year), month: String(month) });
        if (window) {
            q.set('fromYear', String(window.fromYear));
            q.set('fromMonth', String(window.fromMonth));
        }
        const fp = filterParams(filters);
        return fetchApi<BabQuery[]>(`/bab-report/queries?${q}${fp ? `&${fp}` : ''}`);
    },

    getQuery(queryId: number): Promise<BabQueryDetail> {
        return fetchApi<BabQueryDetail>(`/bab-report/queries/${queryId}`);
    },

    /**
     * The anchor is derived server-side from the target, exactly like a comment:
     * a line query stores the current report filters, an account/booking query the
     * drilled row's own company / BU / account.
     */
    createQuery(
        year: number, month: number,
        target: BabCommentTarget, input: BabQueryCreateInput,
        filters: ReturnType<typeof toApiFilters>
    ): Promise<BabQuery> {
        return fetchApi<BabQuery>('/bab-report/queries', {
            method: 'POST',
            body: JSON.stringify({
                periodYear: year,
                periodMonth: month,
                lineCode: target.lineCode,
                companyCode: target.companyCode ?? filters.babCompanyCode,
                buCode: target.buCode ?? filters.buCode,
                accountCode: target.accountCode,
                transactionRef: target.transactionRef,
                // See createComment: only set for a clicked MTD month.
                refPeriodYear: target.refPeriodYear,
                refPeriodMonth: target.refPeriodMonth,
                ...input,
            }),
        });
    },

    updateQuery(queryId: number, patch: Partial<BabQueryCreateInput>): Promise<BabQuery> {
        return fetchApi<BabQuery>(`/bab-report/queries/${queryId}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        });
    },

    replyToQuery(queryId: number, text: string): Promise<{ messageId: number; query: BabQuery | null }> {
        return fetchApi<{ messageId: number; query: BabQuery | null }>(
            `/bab-report/queries/${queryId}/messages`,
            { method: 'POST', body: JSON.stringify({ text }) }
        );
    },

    /** `reason` is mandatory for 'Rejected' — the server refuses it otherwise. */
    setQueryStatus(queryId: number, status: BabQueryStatus, reason?: string): Promise<BabQuery> {
        return fetchApi<BabQuery>(`/bab-report/queries/${queryId}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status, reason: reason ?? null }),
        });
    },

    /**
     * Withdraw a query raised by mistake. Author or admin, soft delete — not the same as
     * "Rejected", which is an assignee judging the question not relevant and stays visible.
     */
    withdrawQuery(queryId: number): Promise<{ queryId: number; withdrawn: boolean }> {
        return fetchApi<{ queryId: number; withdrawn: boolean }>(
            `/bab-report/queries/${queryId}`, { method: 'DELETE' }
        );
    },

    /**
     * Turn one reply into a permanent comment at the same anchor. Author or admin.
     * A 409 comes back when the query already has a linked comment — the message names
     * it, so the pane can show "already saved" instead of a failure.
     */
    queryAnswerToComment(queryId: number, messageId: number): Promise<{
        queryId: number; commentId: number; messageId: number; anchorLevel: BabQueryAnchorLevel;
    }> {
        return fetchApi<{
            queryId: number; commentId: number; messageId: number; anchorLevel: BabQueryAnchorLevel;
        }>(`/bab-report/queries/${queryId}/to-comment`, {
            method: 'POST',
            body: JSON.stringify({ messageId }),
        });
    },

    /**
     * "Open points per company" for the closing call. A plain fetch rather than
     * fetchApi, because the body is a workbook and not the JSON envelope.
     *
     * The server applies the same booking-detail filter as the list, so two people
     * downloading the same period legitimately get different row counts.
     */
    async exportOpenQueries(year: number, month: number): Promise<void> {
        const res = await fetch(`/api/v1/bab-report/queries/export?year=${year}&month=${month}&format=xlsx`);
        if (!res.ok) throw new Error(`Export failed: ${res.status}`);
        const url = URL.createObjectURL(await res.blob());
        const a = document.createElement('a');
        a.href = url;
        a.download = `BAB_OpenQueries_${year}-${String(month).padStart(2, '0')}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
    },

    // ---- Attachments (phase 3A) ----

    /**
     * Attach a screenshot or a document to the question (`messageId` omitted) or to one
     * reply. The file travels base64-encoded in the JSON body, like an expense receipt.
     *
     * The server enforces the type allowlist (PDF / PNG / JPEG) and the 10 MB limit and
     * answers with a sentence to show verbatim — the accept attribute below is only a
     * convenience, and an iPhone hands HEIC through with an empty `file.type`.
     */
    addQueryAttachment(queryId: number, file: {
        fileName: string; contentType: string; fileBase64: string; messageId?: number | null;
    }): Promise<BabQueryAttachment & { duplicate: boolean }> {
        return fetchApi<BabQueryAttachment & { duplicate: boolean }>(
            `/bab-report/queries/${queryId}/attachments`,
            { method: 'POST', body: JSON.stringify(file) }
        );
    },

    /** Mint a ~10 minute read URL. Never cached, never stored — fetched per click. */
    getQueryAttachmentUrl(attachmentId: number): Promise<{
        url: string; fileName: string; contentType: string; expiresInSeconds: number;
    }> {
        return fetchApi<{
            url: string; fileName: string; contentType: string; expiresInSeconds: number;
        }>(`/bab-report/attachments/${attachmentId}/url`);
    },

    /** Soft delete. Uploader or admin; the thread keeps the fact that it was there. */
    removeQueryAttachment(attachmentId: number): Promise<{ attachmentId: number; removed: boolean }> {
        return fetchApi<{ attachmentId: number; removed: boolean }>(
            `/bab-report/attachments/${attachmentId}`, { method: 'DELETE' }
        );
    },

    /**
     * Earlier queries on the same position, newest period first. Pass company + account
     * for an account/booking anchor; omit both for the line-level one.
     */
    getQueryHistory(opts: {
        lineCode: string;
        periodYear: number;
        periodMonth: number;
        sourceCompany?: string | null;
        accountCode?: string | null;
    }): Promise<BabQueryHistoryItem[]> {
        const q = new URLSearchParams({
            lineCode: opts.lineCode,
            periodYear: String(opts.periodYear),
            periodMonth: String(opts.periodMonth),
        });
        if (opts.sourceCompany && opts.accountCode) {
            q.set('sourceCompany', opts.sourceCompany);
            q.set('accountCode', opts.accountCode);
        }
        return fetchApi<BabQueryHistoryItem[]>(`/bab-report/queries/history?${q}`);
    },

    getQueryInbox(opts: {
        scope?: 'assigned' | 'raised' | 'all';
        status?: BabQueryStatus | 'open';
        overdue?: boolean;
    } = {}): Promise<BabQueryInboxItem[]> {
        const q = new URLSearchParams({ scope: opts.scope || 'assigned' });
        if (opts.status) q.set('status', opts.status);
        if (opts.overdue) q.set('overdue', '1');
        return fetchApi<BabQueryInboxItem[]>(`/bab-report/queries/inbox?${q}`);
    },

    /** Just the number, for the badge. Same filter as the inbox. */
    getQueryInboxCount(scope: 'assigned' | 'raised' | 'all' = 'assigned'): Promise<{ total: number }> {
        return fetchApi<{ total: number }>(
            `/bab-report/queries/inbox?scope=${scope}&status=open&countOnly=1`
        );
    },

    /**
     * The people picker. Pass the anchor's source company for a booking-level query so
     * each candidate carries whether they may open that posting — the ones who may not
     * cannot be made responsible (the server refuses them too).
     */
    getAssignableUsers(sourceCompany?: string): Promise<BabAssignableUser[]> {
        const q = sourceCompany ? `?sourceCompany=${encodeURIComponent(sourceCompany)}` : '';
        return fetchApi<BabAssignableUser[]>(`/bab-report/queries/assignable-users${q}`);
    },

    // ---- Admin: booking-detail scope per user (both endpoints are admin-only) ----

    getDetailAccess(): Promise<BabDetailAccessMatrix> {
        return fetchApi<BabDetailAccessMatrix>('/bab-report/detail-access');
    },

    /** Set semantics: `sourceCompanies` replaces that user's whole scope. */
    saveDetailAccess(userEmail: string, sourceCompanies: string[]): Promise<{ userEmail: string; sourceCompanies: string[] }> {
        return fetchApi<{ userEmail: string; sourceCompanies: string[] }>('/bab-report/detail-access', {
            method: 'PUT',
            body: JSON.stringify({ userEmail, sourceCompanies }),
        });
    },
};
